// DAS-3237: Account -> "Advice pipeline". The owner's overview of what produces the
// daily LLM advice: the flow diagram (data blocks -> shared context -> debate steps ->
// code checks -> stored advice) and the full map (which model per step, which prompt
// parts, which tables and scripts behind each data block). Both come from
// GET /api/advice_pipeline_map, which serves two files GENERATED from the pipeline
// source by pythonscripts/scripts/advice_pipeline_map.py on every commit — so this
// page cannot drift from the code.
// DAS-3238: hovering a box shows its latest content — a shared-context block the text the model
// read in the latest run, a debate step or code check that step's result. The box positions come
// from the generated hover file (data.hover), the texts from GET /api/advice_pipeline_content/<user>.
import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert, Typography, Popover } from 'antd';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import { hoverText, zoneStyle } from '../functions/advicePipelineHover'; // DAS-3238

const { Text } = Typography;

const ZONE_CSS = '.apm-zone{position:absolute;border-radius:4px;cursor:help}'
  + '.apm-zone:hover{background:rgba(24,144,255,0.10);outline:1px solid rgba(24,144,255,0.45)}';

const AdvicePipelineModal = ({ isVisible, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);   // DAS-3238
  // DAS-2557/2620: prefer the reactive auth userId; getUserId() lags on first login.
  const authUserId = useSelector((state) => state?.auth?.userId);
  const userId = authUserId || getUserId();

  useEffect(() => {
    if (!isVisible || !userId) return;
    let cancelled = false;
    api.get(`/advice_pipeline_content/${userId}`)
      .then((res) => { if (!cancelled) setContent(res.data || {}); })
      .catch(() => { if (!cancelled) setContent({ steps: {}, blocks: {}, blocks_stored: false }); });
    return () => { cancelled = true; };
  }, [isVisible, userId]);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get('/advice_pipeline_map')
      .then((res) => { if (!cancelled) setData(res.data || {}); })
      .catch((e) => {
        if (cancelled) return;
        const msg = (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'failed to load';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isVisible]);

  const svgSrc = data && data.svg ? `data:image/svg+xml;utf8,${encodeURIComponent(data.svg)}` : null;

  return (
    <Modal
      title="Advice pipeline — what produces the morning advice"
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={1180}
      style={{ top: 20 }}
      data-testid="advice-pipeline-modal"
    >
      <div data-testid="advice-pipeline-body">
        {loading && <Spin />}
        {error && <Alert type="error" message={error} showIcon />}
        {data && !loading && !error && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Generated from the pipeline code{data.generated ? ` (file written ${data.generated})` : ''}. It is regenerated on every commit, so what you read here is what runs.
            </Text>
            {svgSrc && (
              <div style={{ position: 'relative', border: '1px solid #e5e5e5', borderRadius: 6, marginBottom: 16 }}>
                <style>{ZONE_CSS}</style>
                <img
                  src={svgSrc}
                  alt="Flow of the morning advice"
                  data-testid="advice-pipeline-diagram"
                  style={{ width: '100%', display: 'block', borderRadius: 6 }}
                />
                {data.hover && (data.hover.zones || []).map((z) => (
                  <Popover
                    key={`${z.kind}-${z.key}`}
                    trigger="hover"
                    mouseEnterDelay={0.15}
                    placement={z.kind === 'block' ? 'right' : 'left'}
                    title={`${z.label}${content && content.epoch ? ` — latest run ${new Date(content.epoch * 1000).toLocaleString()}` : ''}`}
                    content={(
                      <div data-testid="pipeline-hover-content"
                           style={{ maxWidth: 640, maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                        {hoverText(z, content)}
                      </div>
                    )}
                  >
                    <div className="apm-zone" data-testid={`pipeline-hover-${z.key}`} style={zoneStyle(z, data.hover)} />
                  </Popover>
                ))}
              </div>
            )}
            <div className="advice-pipeline-markdown" style={{ fontSize: 13, lineHeight: 1.45 }}>
              <ReactMarkdown
                components={{
                  table: ({ node, ...props }) => (
                    <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12 }} {...props} />
                  ),
                  th: ({ node, ...props }) => (
                    <th style={{ border: '1px solid #ddd', padding: '4px 6px', background: '#fafafa', textAlign: 'left' }} {...props} />
                  ),
                  td: ({ node, ...props }) => (
                    <td style={{ border: '1px solid #ddd', padding: '4px 6px', verticalAlign: 'top' }} {...props} />
                  ),
                }}
              >
                {data.markdown || ''}
              </ReactMarkdown>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AdvicePipelineModal;
